import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  onSnapshot,
  Timestamp,
  deleteDoc,
  doc,
  getDocsFromCache,
  getDocsFromServer,
  serverTimestamp,
  increment,
  updateDoc
} from 'firebase/firestore';
import { db } from './config';
import { Service, Cost } from '../types';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
let servicesCache: Record<string, Service[]> = {};
let cacheTimestamp: Record<string, number> = {};

const normalizeDate = (dateInput: any): Date => {
  if (!dateInput) return new Date();
  if (dateInput instanceof Date) return dateInput;
  if (dateInput && typeof dateInput.toDate === 'function') {
    return dateInput.toDate();
  }
  const parsed = new Date(dateInput);
  if (!isNaN(parsed.getTime())) return parsed;
  return new Date();
};

export const addService = async (userId: string, service: Omit<Service, 'id' | 'userId' | 'createdAt'>) => {
  try {
    // Salva apenas campos definidos (compatível com versões antigas)
    const serviceDate = normalizeDate(service.date);
    const serviceData: any = {
      userId,
      description: service.description,
      client: service.client || null,
      value: service.value,
      profitMargin: service.profitMargin,
      date: Timestamp.fromDate(serviceDate),
      // Use serverTimestamp for authoritative created/updated times e rev para versions
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      rev: 1,
    };
    // Campos detalhados (Premium)
    if (service.type) serviceData.type = service.type;
    if (service.category) serviceData.category = service.category;
    if (service.notes) serviceData.notes = service.notes;
    if (service.recurrence) serviceData.recurrence = service.recurrence;

    const docRef = await addDoc(collection(db, 'services'), serviceData);

    // Invalida cache
    delete servicesCache[userId];
    delete cacheTimestamp[userId];

    return docRef.id;
  } catch (error: any) {
    console.error('Erro ao adicionar serviço:', error);
    throw new Error('Não foi possível salvar o serviço. Verifique sua conexão.');
  }
};

export const getServices = async (userId: string, startDate?: Date, endDate?: Date): Promise<Service[]> => {
  try {
    // Verifica cache em memória
    const cacheKey = `${userId}-${startDate?.getTime()}-${endDate?.getTime()}`;
    const now = Date.now();
    if (servicesCache[cacheKey] && cacheTimestamp[cacheKey]) {
      const age = now - cacheTimestamp[cacheKey];
      if (age < CACHE_DURATION) {
        return servicesCache[cacheKey];
      }
    }

    // Monta query (sem limite para garantir carregamento completo dos dados do usuário)
    let q = query(
      collection(db, 'services'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    if (startDate && endDate) {
      q = query(
        collection(db, 'services'),
        where('userId', '==', userId),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'desc')
      );
    }

    // Busca do servidor SEMPRE primeiro
    let snapshot;
    try {
      snapshot = await getDocsFromServer(q);
    } catch (serverError) {
      // Se falhar, tenta cache do Firestore
      try {
        snapshot = await getDocsFromCache(q);
      } catch (cacheError) {
        throw serverError;
      }
    }

    const services = snapshot.docs.map((doc: any) => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        description: data.description,
        client: data.client || undefined,
        value: data.value,
        profitMargin: data.profitMargin,
        date: data.date ? data.date.toDate() : new Date(),
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        // Campos detalhados (Premium)
        type: data.type,
        category: data.category,
        notes: data.notes,
        recurrence: data.recurrence,
      } as Service;
    });
    // Salva no cache em memória
    servicesCache[cacheKey] = services;
    cacheTimestamp[cacheKey] = now;
    return services;
  } catch (error: any) {
    console.error('Erro ao buscar serviços:', error);
    // Se offline, retorna cache vazio mas não falha
    if (error.code === 'unavailable' || error.message?.includes('offline')) {
      const cacheKey = `${userId}-${startDate?.getTime()}-${endDate?.getTime()}`;
      return servicesCache[cacheKey] || [];
    }
    throw new Error('Erro ao carregar serviços.');
  }
};

// Real-time subscription to user services (for cross-browser immediate update)
export const subscribeServices = (userId: string, callback: (services: Service[]) => void, onError?: (error: any) => void) => {
  const q = query(
    collection(db, 'services'),
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const services = snapshot.docs.map((doc: any) => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        description: data.description,
        client: data.client || undefined,
        value: data.value,
        profitMargin: data.profitMargin,
        date: data.date ? data.date.toDate() : new Date(),
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        type: data.type,
        category: data.category,
        notes: data.notes,
        recurrence: data.recurrence,
      } as Service;
    });
    callback(services);
  }, (error) => {
    if (onError) onError(error);
  });
};

export const deleteService = async (serviceId: string) => {
  try {
    await deleteDoc(doc(db, 'services', serviceId));
    // Invalida todo o cache
    servicesCache = {};
    cacheTimestamp = {};
  } catch (error: any) {
    console.error('❌ Erro ao deletar serviço:', error);
    throw new Error('Erro ao deletar serviço. Tente novamente.');
  }
};

export const addCost = async (userId: string, cost: Omit<Cost, 'id' | 'userId' | 'createdAt'>) => {
  const costData = {
    userId,
    ...cost,
    date: Timestamp.fromDate(cost.date),
    createdAt: Timestamp.fromDate(new Date())
  };
  
  const docRef = await addDoc(collection(db, 'costs'), costData);
  return docRef.id;
};

export const getCosts = async (userId: string, startDate?: Date, endDate?: Date): Promise<Cost[]> => {
  let q = query(
    collection(db, 'costs'),
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  if (startDate && endDate) {
    q = query(
      collection(db, 'costs'),
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    date: doc.data().date.toDate(),
    createdAt: doc.data().createdAt.toDate()
  } as Cost));
};

export const deleteCost = async (costId: string) => {
  await deleteDoc(doc(db, 'costs', costId));
};

// Categories: simple per-user category list used for autocompletion
export const addCategory = async (userId: string, name: string) => {
  try {
    const docRef = await addDoc(collection(db, 'categories'), { userId, name, createdAt: Timestamp.fromDate(new Date()) });
    return docRef.id;
  } catch (err: any) {
    console.error('Erro ao adicionar categoria:', err);
    throw new Error('Não foi possível salvar a categoria.');
  }
};

export const getCategories = async (userId: string): Promise<string[]> => {
  try {
    const q = query(collection(db, 'categories'), where('userId', '==', userId), orderBy('name', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => (d.data() as any).name as string);
  } catch (err) {
    console.error('Erro ao buscar categorias:', err);
    return [];
  }
};

export const updateService = async (serviceId: string, updates: Partial<Service>) => {
  try {
    const serviceRef = doc(db, 'services', serviceId);
    // Remove campos não permitidos
    const { id, userId, createdAt, ...allowedUpdates } = updates as any;
    // Normalize date se existir
    if (allowedUpdates.date) {
      allowedUpdates.date = Timestamp.fromDate(normalizeDate(allowedUpdates.date));
    }
    // Use serverTimestamp and increment for rev
    await updateDoc(serviceRef, { ...allowedUpdates, updatedAt: serverTimestamp(), rev: increment(1) } as any);
    // Invalida todo o cache
    servicesCache = {};
    cacheTimestamp = {};
  } catch (error: any) {
    console.error('Erro ao atualizar serviço:', error);
    throw new Error('Erro ao atualizar serviço. Tente novamente.');
  }
};
